import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  ProjectStatus,
  TimelineContainerModel,
} from '../../../../../typings/api/skymap/rest/v0/.common';
import {
  AssetTagModel,
  GroupModel,
  InvitedUserModel,
  ProjectModel as ProjectModelV1,
  TagModel,
  UserModel,
} from '../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { FeatureFlagsManager } from '../../../utils/core/feature-flags-manager';
import { isDefined } from '../../../utils/variables';
import { Button } from '../../components/button/button';
import { DroneLoader } from '../../components/drone-loader/drone-loader';
import { useNonce } from '../../hooks/use-nonce';
import { useSharedView } from '../../hooks/use-shared-view';
import { getSelectedProjectId, getSharedFolderId } from '../../routing/utils';
import { Center } from '../../styles/center';
import { reset } from '../../utils/styled-reset';
import { useOptionalAuthState } from '../auth-state';
import { ProjectStateAdapterBridge } from './adapter/project-state-adapter-bridge';

const isProjectUserGroup = (group: GroupModel) => {
  return group.scope === 'PROJECT';
};
const isCompanyAdminGroup = (group: GroupModel) => {
  return group.scope === 'COMPANY';
};

const isInvitedUser = (user: UserModel | InvitedUserModel): user is InvitedUserModel =>
  user.status === 'invited';
const isVerifiedUser = (user: UserModel | InvitedUserModel): user is UserModel =>
  !isInvitedUser(user);

export interface Project {
  id: string;
  name: string;
  description: string;
  tags: TagModel[];
  assetTags: AssetTagModel[];
  companyId: string | null;
  status: ProjectStatus;
  validToDate: Date | null;
  companyAdmins: UserModel[];
  users: UserModel[];
  invitedUsers: InvitedUserModel[];
  invitedCompanyAdmins?: InvitedUserModel[];
  timelineContainers?: TimelineContainerModel[];
  /** Alias for isCompanyAccountOverridingDesignToolAccess; used by UserStore etc. */
  isProjectOverridingDesignToolAccess?: boolean;
}

export interface ProjectContext {
  project: Project;
  sharedFolderId?: string;
  refreshProjecName(this: void, name: string): void;
  refreshProjectDescription(this: void, description: string): void;
  reloadProject(this: void): Promise<void>;
  reloadAssetTags(this: void): Promise<void>;
  isCompanyAdmin(this: void, userId?: string): boolean;
  isOrganizationAdmin(this: void, userId?: string): boolean;
  isAdmin(this: void, userId?: string): boolean;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const ProjectContext = React.createContext<ProjectContext>(undefined!);

type Props = {
  children: React.ReactNode;
  displayLoading?: boolean;
  loadingMode?: 'fullscreen' | 'page-content';
  message?: string;
};

enum ProjectRequestStatus {
  RequestPending,
  Success,
  Failure,
}

/**
 * Renders children passed in via props only when current project (project id in URL)
 * has been successfully fetched from API. Displays loading text and failure text if
 * project could not be fetched from API.
 */
const ProjectState = (props: Props) => {
  const { t } = useTranslation();
  const { isSharedView } = useSharedView();
  const authState = useOptionalAuthState();
  const [projectStatus, setProjectStatus] = React.useState(ProjectRequestStatus.RequestPending);
  const [project, setProject] = React.useState<Project>();
  const [sharedFolderId, setSharedFolderId] = React.useState<string>();
  const [refreshNonce, setRefreshNonce] = useNonce();

  const loadSharedFolderData = React.useCallback(async (sharedFolderId: string) => {
    setSharedFolderId(sharedFolderId);

    const projectResponse = await SkyMapAxiosServiceFactory.instance
      .createSharedFolderServiceV1()
      .getSharedFolder({ path: { sharedFolderId } });
    setProject({
      id: projectResponse.data.projectId,
      name: projectResponse.data.projectName,
      description: '',
      tags: [] as TagModel[],
      assetTags: [] as AssetTagModel[],
      companyId: null,
      status: 'ACTIVE',
      validToDate: null,
      companyAdmins: [],
      users: [],
      invitedUsers: [],
      invitedCompanyAdmins: [],
      timelineContainers: [],
    });

    setProjectStatus(ProjectRequestStatus.Success);
  }, []);

  const reloadAssetTagsInternal = React.useCallback(async (projectIdOverride?: string) => {
    const currentProjectId = projectIdOverride ?? getSelectedProjectId();
    if (!currentProjectId) {
      return;
    }

    try {
      const { data } = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV1()
        .getAssetTags({ path: { projectId: currentProjectId } });

      setProject((prev) => (prev ? { ...prev, assetTags: data } : prev));
    } catch {
      // Best-effort; keep previous state on failure
    }
  }, []);

  const reloadAssetTags = React.useCallback(
    async () => reloadAssetTagsInternal(),
    [reloadAssetTagsInternal],
  );

  const projectId = getSelectedProjectId();
  const sharedId = getSharedFolderId();

  const {
    data: projectQueryData,
    refetch: refetchProject,
    isPending: isProjectLoading,
    error: projectError,
  } = useQuery({
    queryKey: ['loadProject', projectId, refreshNonce],
    queryFn: async () => {
      if (!isDefined(projectId)) {
        throw new Error('Project ID is required');
      }
      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
      const projectResponsePromise = projectService.getProject({
        path: { projectId },
        query: { include: ['projects.company', 'projects.tags'] },
      }) as Promise<{ data: ProjectModelV1 }>;
      const userGroupsPromise = (async (): Promise<GroupModel[]> => {
        const response = (await projectService.getProjectUserGroups({
          path: { projectId },
          query: { include: ['user-groups.users'] },
        })) as { data: GroupModel[] };
        return response.data;
      })();
      const projectResponse = await projectResponsePromise;
      const projectData: ProjectModelV1 = projectResponse.data;
      const userGroups = await userGroupsPromise;

      return {
        projectData,
        userGroups,
      };
    },
    enabled: isDefined(projectId) && !isSharedView,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  React.useEffect(() => {
    if (projectQueryData) {
      const { projectData, userGroups } = projectQueryData;
      setSharedFolderId(undefined);

      // A group's users can contain both verified users and pending invitations
      // (InvitedUserModel). Split them so consumers get clean, verified arrays.
      const companyGroupUsers = userGroups
        .filter((group) => isCompanyAdminGroup(group))
        .flatMap((group) => group.users ?? []);
      const projectGroupUsers = userGroups
        .filter((group) => isProjectUserGroup(group))
        .flatMap((group) => group.users ?? []);

      setProject({
        id: projectData.id,
        name: projectData.name,
        description: projectData.description ?? '',
        tags: projectData.tags ?? [],
        assetTags: [] as AssetTagModel[],
        companyId: projectData.companyId,
        status: projectData.status,
        validToDate: null,
        companyAdmins: companyGroupUsers.filter(isVerifiedUser),
        invitedCompanyAdmins: companyGroupUsers.filter(isInvitedUser),
        users: projectGroupUsers.filter(isVerifiedUser),
        invitedUsers: projectGroupUsers.filter(isInvitedUser),
        timelineContainers: [],
      });
      void reloadAssetTagsInternal(projectData.id);
    }
  }, [projectQueryData, reloadAssetTagsInternal]);

  React.useEffect(() => {
    const load = async () => {
      try {
        if (isSharedView) {
          setProjectStatus(ProjectRequestStatus.Success);
          return;
        }

        if (projectId) {
          if (isProjectLoading) {
            setProjectStatus(ProjectRequestStatus.RequestPending);
          } else if (projectError) {
            setProjectStatus(ProjectRequestStatus.Failure);
          } else if (projectQueryData) {
            // TODO: remove artificial delay (drone loader review)
            await new Promise((resolve) => setTimeout(resolve, 500));
            setProjectStatus(ProjectRequestStatus.Success);
          }
        } else if (sharedId) {
          await loadSharedFolderData(sharedId);
        } else {
          setProjectStatus(ProjectRequestStatus.Failure);
        }
      } catch (err) {
        setProjectStatus(ProjectRequestStatus.Failure);
      }
    };

    void load();
  }, [
    refreshNonce,
    isSharedView,
    projectId,
    sharedId,
    isProjectLoading,
    projectError,
    projectQueryData,
    loadSharedFolderData,
  ]);

  const reloadProject = React.useCallback(async () => {
    try {
      await refetchProject();
    } catch (err) {
      setProjectStatus(ProjectRequestStatus.Failure);
    }
  }, [refetchProject]);

  const isOrganizationAdmin = React.useCallback(() => {
    return authState?.user?.isOrganizationAdmin ?? false;
  }, [authState?.user]);

  const isCompanyAdmin = React.useCallback(
    (userId?: string) => {
      const idToCheck = userId ?? authState?.user?.id;

      return (
        isDefined(idToCheck) &&
        (project?.companyAdmins?.some((user) => user.id === idToCheck) ?? false)
      );
    },
    [authState?.user?.id, project?.companyAdmins],
  );

  const isAdmin = React.useCallback(
    () => isOrganizationAdmin() || isCompanyAdmin(authState?.user?.id),
    [isCompanyAdmin, isOrganizationAdmin, authState?.user?.id],
  );

  switch (projectStatus) {
    case ProjectRequestStatus.RequestPending:
      if (!(props.displayLoading ?? true)) {
        return null;
      }
      return FeatureFlagsManager.instance.isEnabled('SK-6974-drone-loader') ? (
        <DroneLoader message={props.message} mode={props.loadingMode ?? 'fullscreen'} />
      ) : (
        <Center>{t('oneMoment', { ns: 'common' })}</Center>
      );

    case ProjectRequestStatus.Success:
      if (!project) {
        return null;
      }

      return (
        <ProjectContext.Provider
          value={{
            project: project,
            sharedFolderId: sharedFolderId,
            refreshProjecName: (name) => {
              if (project) {
                setProject({
                  ...project,
                  name,
                });
              }
            },
            refreshProjectDescription: (description) => {
              if (project) {
                setProject({
                  ...project,
                  description,
                });
              }
            },
            reloadProject,
            reloadAssetTags,
            isCompanyAdmin,
            isOrganizationAdmin,
            isAdmin,
          }}
        >
          <ProjectStateAdapterBridge />
          {props.children}
        </ProjectContext.Provider>
      );
    case ProjectRequestStatus.Failure:
      return (
        <Center>
          <ErrorContainer>
            {!sharedFolderId && (
              <>
                <h3>Oväntat fel</h3>
                <span>Det gick inte att hämta information om projektet.</span>
              </>
            )}

            {sharedFolderId && (
              <>
                <h3>Oväntat fel</h3>
                <span>Mappen delas inte.</span>
              </>
            )}
            <Button color="primary" variant="contained" onClick={() => setRefreshNonce()}>
              Försök igen
            </Button>
          </ErrorContainer>
        </Center>
      );
  }
};

export { ProjectState };

const ErrorContainer = styled.div`
  ${reset}
  font-size: 14px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5em;
`;
